import { Injectable, computed, signal } from '@angular/core';
import { Patient } from '../patient/patient.models';

/**
 * Patient Cache Service - Centralized patient data management with signals only
 * Provides real-time synchronization across all modules (requester/expert)
 */
@Injectable({ providedIn: 'root' })
export class PatientCacheService {
    
    // Central patient cache with signals
    private _patientCache = signal<Map<string, Patient>>(new Map());
    private _lastUpdated = signal<string | null>(null);
    private _updateTrigger = signal<number>(0);
    
    // Public readonly signals
    public readonly patientCache = this._patientCache.asReadonly();
    public readonly lastUpdated = this._lastUpdated.asReadonly();
    public readonly updateTrigger = this._updateTrigger.asReadonly();
    
    // Computed signal for cache statistics
    public readonly cacheStats = computed(() => {
        const cache = this._patientCache();
        return {
            totalCached: cache.size,
            lastUpdated: this._lastUpdated(),
            updateCount: this._updateTrigger()
        };
    });

    constructor() {
        console.log('PatientCacheService initialized');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Cache Management Methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Adds or updates a patient in the cache
     */
    setPatient(patient: Patient): void {
        const cache = new Map(this._patientCache());
        const patientKey = patient.id || patient.entityId!;
        
        cache.set(patientKey, patient);
        this._patientCache.set(cache);
        this._lastUpdated.set(new Date().toISOString());
        this._updateTrigger.set(this._updateTrigger() + 1);
        
        console.log(`Patient cached: ${patient.fullName} (${patientKey})`);
    }

    /**
     * Gets a patient from the cache
     */
    getPatient(patientId: string): Patient | null {
        const cache = this._patientCache();
        return cache.get(patientId) || null;
    }

    /**
     * Checks if a patient exists in cache
     */
    hasPatient(patientId: string): boolean {
        const cache = this._patientCache();
        return cache.has(patientId);
    }

    /**
     * Removes a patient from the cache
     */
    removePatient(patientId: string): void {
        const cache = new Map(this._patientCache());
        const removed = cache.delete(patientId);
        
        if (removed) {
            this._patientCache.set(cache);
            this._lastUpdated.set(new Date().toISOString());
            this._updateTrigger.set(this._updateTrigger() + 1);
            
            console.log(`Patient removed from cache: ${patientId}`);
        }
    }

    /**
     * Updates multiple patients in cache (bulk operation)
     */
    setPatients(patients: Patient[]): void {
        const cache = new Map(this._patientCache());
        
        patients.forEach(patient => {
            const patientKey = patient.id || patient.entityId!;
            cache.set(patientKey, patient);
        });
        
        this._patientCache.set(cache);
        this._lastUpdated.set(new Date().toISOString());
        this._updateTrigger.set(this._updateTrigger() + 1);
        
        console.log(`Bulk cached ${patients.length} patients`);
    }

    /**
     * Gets all cached patients as array
     */
    getAllPatients(): Patient[] {
        const cache = this._patientCache();
        return Array.from(cache.values());
    }

    /**
     * Clears the entire patient cache
     */
    clearCache(): void {
        this._patientCache.set(new Map());
        this._lastUpdated.set(null);
        this._updateTrigger.set(this._updateTrigger() + 1);
        console.log('Patient cache cleared');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Computed Signals for Components
    // -----------------------------------------------------------------------------------------------------

    /**
     * Returns a computed signal that tracks a specific patient
     */
    getPatientSignal(patientId: string) {
        return computed(() => {
            // Access updateTrigger to ensure reactivity
            this._updateTrigger();
            return this.getPatient(patientId);
        });
    }

    /**
     * Returns a computed signal for all patients
     */
    getAllPatientsSignal() {
        return computed(() => {
            // Access updateTrigger to ensure reactivity
            this._updateTrigger();
            return this.getAllPatients();
        });
    }
}